import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { PlaytimeUserBadge } from '../../../common/types/playtime.js'
import { BadgeArt } from '../badge-art/badge-art.js'
import { BadgeClaimForm, BadgeClaimFormProps } from '../badge-claim-form/badge-claim-form.js'
import { BadgeRevealStage, RevealPhase } from '../badge-reveal-stage/badge-reveal-stage.js'
import './badge-modal.scss'

export interface BadgeModalProps {
    badge: PlaytimeUserBadge
    lang: Language
    /** Unclaimed badge shown as if activated ("Märgi eelvaade"). */
    isPeeking: boolean
    onTogglePeek: (next: boolean) => void
    /** Fresh badge unlock; `null` when the badge is not new. */
    reveal: { phase: RevealPhase; onRevealEnd: () => void } | null
    /** Present only when the viewer may claim this badge. */
    claim: BadgeClaimFormProps | null
}

// claimed_date arrives as 'YYYY-MM-DD HH:mm:ss'; parse the date part at local midnight.
const formatDate = (iso: string, lang: Language) =>
    new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString(lang === 'et' ? 'et-EE' : 'en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })

export const BadgeModal = ({
    badge,
    lang,
    isPeeking,
    onTogglePeek,
    reveal,
    claim,
}: BadgeModalProps) => {
    const b = t.playtime.badges
    const isClaimed = badge.status === 'claimed'
    const isPending = badge.status === 'pending'
    const locked = !isClaimed && !isPending
    const stateText = isClaimed
        ? badge.claimed_date
            ? b.state.claimed(formatDate(badge.claimed_date, lang))(lang)
            : b.state.active(lang)
        : isPending
          ? b.state.pending(lang)
          : b.state.inactive(lang)
    const showProof = (isClaimed || isPending) && (badge.request_description || badge.request_file)
    const note =
        locked && badge.is_anniversary
            ? b.autoGranted(lang)
            : locked && badge.is_special
              ? b.givenOnly(lang)
              : null

    return (
        <div className="badge-modal">
            <div className="badge-modal__stage-wrap">
                <div className="badge-modal__stage">
                    {reveal ? (
                        <BadgeRevealStage
                            art={badge.art}
                            idPrefix={`modal${badge.id}`}
                            phase={reveal.phase}
                            pointsLabel={b.pointsPop(badge.score)(lang)}
                            onRevealEnd={reveal.onRevealEnd}
                        />
                    ) : (
                        <BadgeArt
                            art={badge.art}
                            state={isClaimed || isPeeking ? 'active' : 'slot'}
                            idPrefix={`modal${badge.id}`}
                            tilt="self"
                            eager
                        />
                    )}
                </div>
                {!isClaimed && !reveal && (
                    // Prototype's compact switch (#sh-peek), not the form Toggle.
                    <button
                        type="button"
                        className="badge-modal__peek"
                        role="switch"
                        aria-checked={isPeeking}
                        onClick={() => onTogglePeek(!isPeeking)}
                    >
                        <span className="badge-modal__peek-track" aria-hidden="true">
                            <span className="badge-modal__peek-thumb" />
                        </span>
                        <span className="badge-modal__peek-label">{b.preview(lang)}</span>
                    </button>
                )}
            </div>

            <div className="badge-modal__meta">
                {badge.categories.length > 0 && (
                    <p className="badge-modal__eyebrow">
                        {badge.categories
                            .map((c) => (lang === 'et' ? c.name_et : c.name_en))
                            .join(' · ')}
                    </p>
                )}
                <h2 className="badge-modal__name">{badge.name}</h2>
                <div className="badge-modal__chips">
                    <span className="badge-modal__chip">{b.score(badge.score)(lang)}</span>
                    <span className="badge-modal__chip">
                        <i
                            className={`badge-modal__tier badge-modal__tier--${badge.art.tier}`}
                            aria-hidden="true"
                        />
                        {b.tier[badge.art.tier](lang)}
                    </span>
                    <span className="badge-modal__chip">{stateText}</span>
                    {badge.is_special && (
                        <span className="badge-modal__chip">{b.special(lang)}</span>
                    )}
                </div>
                {badge.description && (
                    <div
                        className="badge-modal__description"
                        dangerouslySetInnerHTML={{ __html: badge.description }}
                    />
                )}
                {showProof && (
                    <div className="badge-modal__proof">
                        {badge.request_description && (
                            <div>
                                <p className="badge-modal__label">{b.comment(lang)}</p>
                                <p className="badge-modal__comment">{badge.request_description}</p>
                            </div>
                        )}
                        {badge.request_file && (
                            <div>
                                <p className="badge-modal__label">{b.evidenceLabel(lang)}</p>
                                <a
                                    href={`/api/playtime/badges/${badge.id}/download`}
                                    className="badge-modal__download"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {b.download(lang)}
                                </a>
                            </div>
                        )}
                    </div>
                )}
                {note && <p className="badge-modal__note">{note}</p>}
                {claim && <BadgeClaimForm {...claim} />}
            </div>
        </div>
    )
}
